import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { useState } from 'react';
import Card from '../../components/ui/Card';
import KpiTile from '../../components/ui/KpiTile';
import PageHeader from '../../components/ui/PageHeader';
import { reportsApi } from '../../services/reportsApi';

const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};
const formatKg = (n: number) => `${n.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg`;
const formatNum = (n: number) => n.toLocaleString();

const ReportsView = () => {
  const [from, setFrom] = useState(daysAgoIso(89));
  const [to, setTo] = useState(todayIso());

  const reportQuery = useQuery({
    queryKey: ['reports', 'monthly', from, to],
    queryFn: async () => (await reportsApi.monthly(from, to)).data,
  });

  const rows = reportQuery.data?.rows ?? [];
  const totalKgs = rows.reduce((acc, r) => acc + r.kgsLoaded, 0);
  const totalTotes = rows.reduce((acc, r) => acc + r.totesLoaded, 0);
  const totalReduced = rows.reduce((acc, r) => acc + r.kgsOffloaded, 0);

  return (
    <>
      <PageHeader
        title="Reports"
        subtitle="Monthly throughput aggregated across all subscribed machines."
        actions={
          <>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <span className="text-slate-500">From</span>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <span className="text-slate-500">To</span>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
              />
            </label>
            <a
              href={reportsApi.exportUrl(from, to)}
              className="inline-flex items-center gap-1.5 rounded-md bg-tfs-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
            >
              <Download className="size-4" aria-hidden /> Export CSV
            </a>
          </>
        }
      />

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <KpiTile label="Rows" value={formatNum(rows.length)} />
        <KpiTile label="Total totes" value={formatNum(totalTotes)} />
        <KpiTile label="Total kgs loaded" value={formatKg(totalKgs)} />
        <KpiTile label="Total kgs offloaded" value={formatKg(totalReduced)} />
      </section>

      <Card className="mt-6" title={`Monthly aggregation · ${rows.length} machine-month rows`}>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="py-2 pr-4 font-medium">Month</th>
                <th className="py-2 pr-4 font-medium">Machine</th>
                <th className="py-2 pr-4 font-medium">Type</th>
                <th className="py-2 pr-4 font-medium">Days</th>
                <th className="py-2 pr-4 font-medium">Totes</th>
                <th className="py-2 pr-4 font-medium">Kgs loaded</th>
                <th className="py-2 pr-4 font-medium">Kgs offloaded</th>
                <th className="py-2 pr-4 font-medium">Load time (h)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {rows.map((r) => (
                <tr key={`${r.machineId}-${r.month}`} className="hover:bg-slate-50">
                  <td className="py-2 pr-4 font-mono text-xs text-slate-500">{r.month}</td>
                  <td className="py-2 pr-4 font-mono text-xs text-slate-800">{r.machineId}</td>
                  <td className="py-2 pr-4">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        r.machineType === 'composter'
                          ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                          : 'bg-sky-50 text-sky-700 ring-1 ring-sky-200'
                      }`}
                    >
                      {r.machineType}
                    </span>
                  </td>
                  <td className="py-2 pr-4">{r.daysWithData}</td>
                  <td className="py-2 pr-4">{r.totesLoaded}</td>
                  <td className="py-2 pr-4">{r.kgsLoaded.toFixed(0)}</td>
                  <td className="py-2 pr-4">
                    {r.machineType === 'composter' ? r.kgsOffloaded.toFixed(0) : '—'}
                  </td>
                  <td className="py-2 pr-4">{(r.timeLoading / 3600).toFixed(1)}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-xs text-slate-400">
                    No data in window.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
};

export default ReportsView;
